import {
  BALANCE,
  CURRENCY,
  ECONOMY,
  FACTIONS,
  MARKET,
  PHASE1_GATE,
  TECHS,
  TRADE_GOODS,
  TRADE_GOOD_IDS,
  type TradeGoodId,
} from '@sd/content';
import {
  Rng,
  createGame,
  hexDistance,
  pocketClouds,
  priceBounds,
  step,
  type Command,
  type GameState,
  type PlayerSetup,
} from '@sd/sim';
import { BotIntel } from './fog';
import { canBuy } from './fleets';
import { plannerBot } from './planner';
import { scoutBot } from './scout';
import { settlerBot } from './settler';
import { contractorBot } from './contractor';
import { researcherBot } from './researcher';
import { patronBot } from './patron';

/**
 * A bot decides which commands to send on a tick. Bots use only the public
 * command API, exactly like human players, so anything a bot can do a player
 * can do and vice versa.
 */
export type Bot = (state: GameState, playerId: string, rng: Rng) => Command[];

/** Phase 0 bot: occasionally spends a pause, then resumes after a few seconds. */
export const pausingBot: Bot = (state, playerId, rng) => {
  if (state.pause?.by === playerId) return rng.chance(0.05) ? [{ type: 'resume', playerId }] : [];
  if (!state.pause && rng.chance(0.0005)) return [{ type: 'pause', playerId }];
  return [];
};

/**
 * Phase 1 bot: a cautious economist. Every few seconds it either sells part
 * of its faction's surplus while the price is near or above base, or buys a
 * good its faction needs while it is near or below base, keeping a cash
 * reserve. It reads only public state (prices, its own holdings).
 */
export const traderBot: Bot = (state, playerId, rng) => {
  if (state.pause || !rng.chance(0.02)) return [];
  const me = state.players.find((p) => p.id === playerId);
  if (!me) return [];
  const faction = FACTIONS[me.faction];
  const ratio = (good: TradeGoodId) => state.market[good].price / TRADE_GOODS[good].basePrice;

  const surplus = faction.cheapGood;
  if (me.goods[surplus] > 10 && ratio(surplus) >= 0.95 && rng.chance(0.5)) {
    const quantity = Math.min(MARKET.maxOrderUnits, rng.int(1, Math.ceil(me.goods[surplus] / 3)));
    return [{ type: 'marketSell', playerId, good: surplus, quantity }];
  }

  const need = rng.pick(faction.needs);
  const price = state.market[need].price;
  // Budget in ГС: a quarter of our cash at the current rate.
  const budget = (me.cash * me.currency.rate * 0.25) / (1 + CURRENCY.conversionFee);
  if (ratio(need) <= 1.05 && budget > price * 1.2 && canBuy(state, playerId, need)) {
    const affordable = Math.floor(budget / (price * (1 + MARKET.fee) * 1.1));
    const quantity = Math.min(MARKET.maxOrderUnits, rng.int(1, Math.max(1, affordable)));
    return [{ type: 'marketBuy', playerId, good: need, quantity }];
  }
  return [];
};

/** Runs several bots for the same player and concatenates their commands. */
export const combineBots =
  (...bots: readonly Bot[]): Bot =>
  (state, playerId, rng) => {
    // Most ticks every bot says nothing: no arrays made for it (#50).
    let out: Command[] | null = null;
    for (const bot of bots) {
      const said = bot(state, playerId, rng);
      if (said.length > 0) (out ??= []).push(...said);
    }
    return out ?? NOTHING;
  };

const NOTHING = Object.freeze([]) as unknown as Command[];

/** Default bot for balance runs: spends pauses and runs its capital. */
export const economistBot: Bot = combineBots(
  pausingBot,
  plannerBot,
  scoutBot,
  settlerBot,
  contractorBot,
  researcherBot,
  patronBot,
);

/** Price of every good relative to its base, sampled at the end of each economic cycle. */
export type PriceHistory = Readonly<Record<TradeGoodId, readonly number[]>>;

export interface MarketReport {
  readonly trades: number;
  readonly unitsTraded: number;
  readonly feesPaid: number;
  /** Gluts and shortages announced, and how many of them hit the price (GDD §6). */
  readonly marketWarnings: number;
  readonly marketShocks: number;
  /** Lowest and highest price seen at any tick, and the final price, relative to base. */
  readonly priceRatio: Readonly<Record<TradeGoodId, { min: number; max: number; final: number }>>;
  readonly priceHistory: PriceHistory;
  /** Human-readable broken market invariants (prices out of bounds, negative holdings). */
  readonly violations: readonly string[];
}

export interface MatchConfig {
  readonly seed: string;
  readonly players: readonly PlayerSetup[];
  /** Simulation ticks to run; defaults to the full session limit. */
  readonly maxSteps?: number;
  /** Bot for every player... */
  readonly bot?: Bot;
  /** ...or one per player, in the order of `players`. */
  readonly bots?: readonly Bot[];
  /** Finds picked from cards (#105, a test). */
  readonly findChoice?: boolean;
}

/** One player at the end of a match. */
export interface PlayerReport {
  readonly id: string;
  readonly faction: PlayerSetup['faction'];
  /** Cash at its rate plus goods at market prices, ГС. */
  readonly netWorth: number;
  /** Everything the planet produced over the match, at base prices, ГС. */
  readonly output: number;
  readonly finalRate: number;
  readonly printed: number;
  readonly finalPopulation: number;
}

/** Whole-economy health over a match: what the phase-1 gate looks at (GDD §15). */
export interface EconomyReport {
  /** Mean over goods of the standard deviation of ln(price / base) across cycles. */
  readonly priceVolatility: number;
  /** The same over the last third of the match: does the market still move late? */
  readonly lateVolatility: number;
  /** Trades per cycle in the last third relative to the whole match. */
  readonly lateActivity: number;
  /**
   * Idle money (#18): the treasury's share of a player's wealth (treasury +
   * stock at market prices), averaged over players and the last third of cycles.
   */
  readonly lateCashShare: number;
  /** Mean rise of the local price level (rate after the first window / final rate − 1). */
  readonly inflation: number;
  readonly maxInflation: number;
  /** Gini coefficient of players' total output, 0 = equal. */
  readonly outputGini: number;
  /** Share of player-cycles with a rate stuck at a bound. */
  readonly ratePinnedShare: number;
  /** Share of good-cycles with a price stuck at a bound. */
  readonly pricePinnedShare: number;
}

/** Gini coefficient of non-negative values. */
export function gini(values: readonly number[]): number {
  const n = values.length;
  const total = values.reduce((a, b) => a + b, 0);
  if (n < 2 || total <= 0) return 0;
  let diff = 0;
  for (const a of values) for (const b of values) diff += Math.abs(a - b);
  return diff / (2 * n * total);
}

const stdDev = (xs: readonly number[]) => {
  if (xs.length < 2) return 0;
  const mean = xs.reduce((a, b) => a + b, 0) / xs.length;
  return Math.sqrt(xs.reduce((a, x) => a + (x - mean) ** 2, 0) / xs.length);
};

export interface MatchReport {
  readonly seed: string;
  readonly playerCount: number;
  readonly mapAttempt: number;
  readonly minCapitalDistance: number;
  readonly minPocketClouds: number;
  readonly clouds: Readonly<Record<1 | 2 | 3, number>>;
  readonly steps: number;
  readonly finalTick: number;
  readonly status: GameState['status'];
  readonly pausesUsed: number;
  readonly rejectedCommands: number;
  /** Rejected commands by type and reason, `marketBuy: embargo` (#64): a bot asking for what it cannot do. */
  readonly rejectedBy: Readonly<Record<string, number>>;
  readonly market: MarketReport;
  readonly planets: PlanetsReport;
  readonly exploration: ExplorationReport;
  readonly research: ResearchReport;
  readonly crises: CrisisReport;
  readonly victory: VictoryReport;
  readonly currency: CurrencyReport;
  readonly economy: EconomyReport;
  readonly players: readonly PlayerReport[];
}

/** How the match ended (GDD §10, #30). */
export interface VictoryReport {
  readonly kind: string | null;
  readonly winner: string | null;
  readonly minute: number;
  /** Countdowns started, by kind. */
  readonly countdowns: Readonly<Record<string, number>>;
}

/** The system against everyone (GDD §9, #28). */
export interface CrisisReport {
  /** Every crisis that struck: its kind, what drove it, the minute it struck. */
  readonly struck: readonly {
    readonly kind: string;
    readonly cause: string;
    readonly minute: number;
  }[];
  /** Index at minute 40 and at the end, and how much each source added over the match. */
  readonly instabilityAt40: number;
  readonly finalInstability: number;
  readonly sources: Readonly<Record<string, number>>;
  /** Project outcomes: failed, threshold, full; and how the final crisis ended. */
  readonly projects: Readonly<Record<string, number>>;
  readonly finalCrisis: 'averted' | 'failed' | null;
  readonly prestige: readonly number[];
}

/** Research over a match (GDD §10, #27). */
export interface ResearchReport {
  /** Technologies each player knows at the end. */
  readonly techsKnown: readonly number[];
  /** Minute of the first tier-3 technology and of the final one, or null. */
  readonly firstTier3Minute: number | null;
  readonly finalTechMinute: number | null;
}

/** Exchange rates of all players' currencies, ГС per unit, sampled every cycle. */
export interface CurrencyReport {
  readonly minRate: number;
  readonly maxRate: number;
  readonly avgFinalRate: number;
  /** Largest gap between two players' rates at the end of the match. */
  readonly finalSpread: number;
  readonly emitted: number;
  /** Reserve currency: how often it was declared and lost, and share of cycles it existed. */
  readonly reserveDeclared: number;
  readonly reserveLost: number;
  readonly reserveCycleShare: number;
  /** First cycle a reserve currency appeared, or null. */
  readonly firstReserveCycle: number | null;
  /** Seigniorage paid to issuers, ГС. */
  readonly seigniorage: number;
}

/** Dust cloud exploration over a match (GDD §7). */
export interface ExplorationReport {
  readonly finds: Readonly<Record<string, number>>;
  /** Minute of the first find, or null. */
  readonly firstFindMinute: number | null;
  readonly cloudsExhausted: number;
  readonly cloudsTotal: number;
  readonly shipsBuilt: number;
  /** Outposts standing at the end, all players, and how many sit on Lagrange points. */
  readonly outposts: number;
  readonly lagrangeOutposts: number;
  readonly lagrangePoints: number;
  /** Contracts signed between players, deliveries made, caravans robbed (GDD §6, §13). */
  readonly contractsSigned: number;
  readonly deliveries: number;
  readonly caravansRobbed: number;
}

/** Averages over every player of the match. */
export interface PlanetsReport {
  readonly finalPopulation: number;
  readonly minPopulation: number;
  /** Mean satisfaction over all cycles, 0..1. */
  readonly avgSatisfaction: number;
  /**
   * Share of player-cycles without enough food, not counting the cycles just
   * after a crisis blow, a raid on the capital or during a siege: hunger the
   * phase-2 mechanics cause on purpose (decision of 19.10.2026).
   */
  readonly hungryShare: number;
  readonly starvationEvents: number;
  readonly constructions: number;
  readonly finalCash: number;
  /** Goods produced per cycle at base prices, ГС, over the whole match. */
  readonly outputPerCycle: number;
}

export function runMatch({
  seed,
  players,
  maxSteps = BALANCE.sessionLimitTicks + BALANCE.pausesPerPlayer * BALANCE.pauseTicks * 4,
  bot = economistBot,
  bots,
  findChoice = false,
}: MatchConfig): MatchReport {
  let state = createGame({ seed, players, findChoice });
  const system = state.system;
  const rng = Rng.fromSeed(`bots:${seed}`);
  const intel = new BotIntel();
  let steps = 0;
  let rejectedCommands = 0;
  const rejectedBy: Record<string, number> = {};
  let trades = 0;
  let unitsTraded = 0;
  let feesPaid = 0;
  let cycles = 0;
  let satisfactionSum = 0;
  let hungry = 0;
  /** Last tick each player's capital took a blow: a crisis, a raid, a siege. */
  const blows = new Map<string, number>();
  let starvationEvents = 0;
  let constructions = 0;
  let outputValue = 0;
  let minPopulation = Infinity;
  let minRate = Infinity;
  let reserveDeclared = 0;
  let reserveLost = 0;
  let reserveCycles = 0;
  let firstReserveCycle: number | null = null;
  let seigniorage = 0;
  const finds: Record<string, number> = {};
  let firstFind: number | null = null;
  let cloudsExhausted = 0;
  let shipsBuilt = 0;
  let contractsSigned = 0;
  let deliveries = 0;
  let caravansRobbed = 0;
  let firstTier3: number | null = null;
  const struck: { kind: string; cause: string; minute: number }[] = [];
  const projects: Record<string, number> = { failed: 0, threshold: 0, full: 0 };
  const countdowns: Record<string, number> = {};
  let instabilityAt40 = 0;
  let finalTech: number | null = null;
  let marketWarnings = 0;
  let marketShocks = 0;
  let maxRate = 0;
  let ratePinned = 0;
  let pricePinned = 0;
  let cycleTrades = 0;
  const tradesPerCycle: number[] = [];
  const cashShares: number[] = [];
  const playerOutput = players.map(() => 0);
  const baselineRate: number[] = [];
  const violations: string[] = [];
  const bounds = Object.fromEntries(TRADE_GOOD_IDS.map((g) => [g, priceBounds(g)])) as Record<
    TradeGoodId,
    { min: number; max: number }
  >;
  const ratio = (s: GameState, g: TradeGoodId) => s.market[g].price / TRADE_GOODS[g].basePrice;
  const range = Object.fromEntries(TRADE_GOOD_IDS.map((g) => [g, { min: 1, max: 1 }])) as Record<
    TradeGoodId,
    { min: number; max: number }
  >;
  const history = Object.fromEntries(TRADE_GOOD_IDS.map((g) => [g, [] as number[]])) as Record<
    TradeGoodId,
    number[]
  >;

  while (steps < maxSteps && state.status === 'running') {
    const commands = players.flatMap((p, i) =>
      (bots?.[i] ?? bot)(intel.view(state, p.id), p.id, rng),
    );
    const result = step(state, commands);
    let traded = false;
    for (const e of result.events) {
      if (e.type === 'crisisDamage') blows.set(e.playerId, result.state.tick);
      if (e.type === 'raided' || e.type === 'siegeStarted') blows.set(e.victim, result.state.tick);
      if (e.type === 'commandRejected') {
        rejectedCommands++;
        const key = `${e.command.type}: ${e.reason}`;
        rejectedBy[key] = (rejectedBy[key] ?? 0) + 1;
      }
      if (e.type === 'traded') {
        traded = true;
        trades++;
        cycleTrades++;
        unitsTraded += e.quote.quantity;
        feesPaid += e.quote.fee;
      }
      if (e.type === 'starvation') starvationEvents++;
      if (e.type === 'traded') seigniorage += e.seigniorage;
      if (e.type === 'reserveDeclared') {
        reserveDeclared++;
        firstReserveCycle ??= Math.round(result.state.tick / BALANCE.cycleTicks);
      }
      if (e.type === 'reserveLost') reserveLost++;
      if (e.type === 'found') {
        finds[e.find] = (finds[e.find] ?? 0) + 1;
        firstFind ??= +((result.state.tick * BALANCE.tickMs) / 60_000).toFixed(1);
      }
      if (e.type === 'cloudExhausted') cloudsExhausted++;
      if (e.type === 'shipBuilt') shipsBuilt++;
      if (e.type === 'contractSigned') contractsSigned++;
      if (e.type === 'delivered') deliveries++;
      if (e.type === 'caravanRobbed') caravansRobbed++;
      if (e.type === 'crisisStruck') {
        const c = state.crises.find((x) => x.id === e.crisisId);
        struck.push({
          kind: e.kind,
          cause: c?.cause ?? 'time',
          minute: +((result.state.tick * BALANCE.tickMs) / 60_000).toFixed(1),
        });
      }
      if (e.type === 'victoryCountdown') countdowns[e.kind] = (countdowns[e.kind] ?? 0) + 1;
      if (e.type === 'projectResolved') projects[e.outcome] = (projects[e.outcome] ?? 0) + 1;
      if (e.type === 'researchCompleted') {
        const minute = +((result.state.tick * BALANCE.tickMs) / 60_000).toFixed(1);
        if (TECHS[e.tech].tier === 3) firstTier3 ??= minute;
        if (TECHS[e.tech].tier === 'final') finalTech ??= minute;
      }
      if (e.type === 'marketEvent' && e.phase === 'warning') marketWarnings++;
      if (e.type === 'marketEvent' && e.phase === 'struck') marketShocks++;
      if (e.type === 'constructionStarted') {
        traded = true; // holdings changed
        constructions++;
      }
    }
    const lastTick = state.tick;
    state = result.state;
    if (state.tick === 40 * 600) instabilityAt40 = state.instability.value;
    steps++;

    // Prices only move on trades and drift toward base, so extremes can only
    // appear on a tick with a trade; holdings likewise change only on trades.
    if (traded) {
      for (const g of TRADE_GOOD_IDS) {
        const price = state.market[g].price;
        const { min, max } = bounds[g];
        if (!Number.isFinite(price) || price < min - 1e-9 || price > max + 1e-9) {
          violations.push(`tick ${state.tick}: ${g} price ${price} outside [${min}, ${max}]`);
        }
        const r = ratio(state, g);
        range[g].min = Math.min(range[g].min, r);
        range[g].max = Math.max(range[g].max, r);
      }
      for (const p of state.players) {
        if (p.cash < 0) violations.push(`tick ${state.tick}: ${p.id} cash ${p.cash}`);
        for (const g of TRADE_GOOD_IDS) {
          if (p.goods[g] < 0) violations.push(`tick ${state.tick}: ${p.id} ${g} ${p.goods[g]}`);
        }
      }
    }
    // The clock stands still during a pause, so sample only when it actually moved.
    if (state.tick !== lastTick && state.tick % BALANCE.cycleTicks === 0) {
      for (const g of TRADE_GOOD_IDS) history[g].push(+ratio(state, g).toFixed(4));
      cycles++;
      if (state.reserve.issuer !== null) reserveCycles++;
      tradesPerCycle.push(cycleTrades);
      cycleTrades = 0;
      cashShares.push(
        state.players.reduce((sum, p) => {
          const cash = Math.max(0, p.cash * p.currency.rate);
          const stock = TRADE_GOOD_IDS.reduce((v, g) => v + p.goods[g] * state.market[g].price, 0);
          return sum + (cash + stock > 0 ? cash / (cash + stock) : 0);
        }, 0) / state.players.length,
      );
      for (const g of TRADE_GOOD_IDS) {
        const { min, max } = bounds[g];
        const price = state.market[g].price;
        if (price <= min * 1.01 || price >= max * 0.99) pricePinned++;
      }
      state.players.forEach((p, i) => {
        const r = p.currency.rate;
        if (r <= CURRENCY.minRate * 1.01 || r >= CURRENCY.maxRate * 0.99) ratePinned++;
        if (cycles === CURRENCY.windowCycles) baselineRate[i] = r;
        const made = p.planet.lastCycle?.produced;
        if (made) {
          for (const g of TRADE_GOOD_IDS) playerOutput[i]! += made[g] * TRADE_GOODS[g].basePrice;
        }
      });
      for (const p of state.players) {
        const report = p.planet.lastCycle;
        if (!report) continue;
        satisfactionSum += report.satisfaction;
        const blow = blows.get(p.id);
        const struck =
          (blow !== undefined &&
            state.tick - blow <= PHASE1_GATE.hungerGraceCycles * BALANCE.cycleTicks) ||
          state.sieges.some((x) => x.victim === p.id);
        if (report.fed.food < 1 && !struck) hungry++;
        minPopulation = Math.min(minPopulation, p.planet.population);
        const rate = p.currency.rate;
        minRate = Math.min(minRate, rate);
        maxRate = Math.max(maxRate, rate);
        if (!Number.isFinite(rate) || rate < CURRENCY.minRate || rate > CURRENCY.maxRate) {
          violations.push(`tick ${state.tick}: ${p.id} rate ${rate}`);
        }
        for (const g of TRADE_GOOD_IDS)
          outputValue += report.produced[g] * TRADE_GOODS[g].basePrice;
        if (!Number.isFinite(p.planet.population) || p.planet.population < ECONOMY.minPopulation) {
          violations.push(`tick ${state.tick}: ${p.id} population ${p.planet.population}`);
        }
        if (p.cash < 0) violations.push(`tick ${state.tick}: ${p.id} cash ${p.cash}`);
        for (const g of TRADE_GOOD_IDS) {
          if (p.goods[g] < 0) violations.push(`tick ${state.tick}: ${p.id} ${g} ${p.goods[g]}`);
        }
      }
    }
    if (violations.length > 20) break;
  }

  const capitals = system.planets.filter((p) => p.homeOf !== null).map((p) => p.hex);
  let minCapitalDistance = Infinity;
  for (let i = 0; i < capitals.length; i++) {
    for (let j = i + 1; j < capitals.length; j++) {
      minCapitalDistance = Math.min(minCapitalDistance, hexDistance(capitals[i]!, capitals[j]!));
    }
  }
  const clouds = { 1: 0, 2: 0, 3: 0 };
  for (const c of system.clouds) clouds[c.level]++;

  return {
    seed,
    playerCount: players.length,
    mapAttempt: system.attempt,
    minCapitalDistance,
    minPocketClouds: Math.min(...capitals.map((c) => pocketClouds(system, c).length)),
    clouds,
    steps,
    finalTick: state.tick,
    status: state.status,
    pausesUsed: state.players.reduce((sum, p) => sum + (BALANCE.pausesPerPlayer - p.pausesLeft), 0),
    rejectedCommands,
    rejectedBy,
    planets: (() => {
      const n = players.length;
      const playerCycles = Math.max(1, cycles * n);
      const mean = (f: (p: (typeof state.players)[number]) => number) =>
        state.players.reduce((sum, p) => sum + f(p), 0) / n;
      return {
        finalPopulation: +mean((p) => p.planet.population).toFixed(2),
        minPopulation: +(Number.isFinite(minPopulation) ? minPopulation : 0).toFixed(2),
        avgSatisfaction: +(satisfactionSum / playerCycles).toFixed(3),
        hungryShare: +(hungry / playerCycles).toFixed(3),
        starvationEvents,
        constructions,
        finalCash: +mean((p) => p.cash).toFixed(2),
        outputPerCycle: +(outputValue / playerCycles).toFixed(1),
      };
    })(),
    exploration: {
      finds,
      firstFindMinute: firstFind,
      cloudsExhausted,
      cloudsTotal: system.clouds.length,
      shipsBuilt,
      outposts: state.outposts.length,
      lagrangeOutposts: state.outposts.filter((o) => o.kind === 'lagrangeStation').length,
      lagrangePoints: system.lagrangePoints.length,
      contractsSigned,
      deliveries,
      caravansRobbed,
    },
    victory: {
      kind: state.result?.kind ?? null,
      winner: state.result?.winner ?? null,
      minute: +((state.tick * BALANCE.tickMs) / 60_000).toFixed(1),
      countdowns,
    },
    crises: {
      struck,
      instabilityAt40: +instabilityAt40.toFixed(1),
      finalInstability: +state.instability.value.toFixed(1),
      projects,
      finalCrisis: state.finalCrisis,
      prestige: state.players.map((p) => p.prestige),
      sources: Object.fromEntries(
        Object.entries(state.instability.total).map(([k, v]) => [k, +v.toFixed(1)]),
      ),
    },
    research: {
      techsKnown: state.players.map((p) => p.techs.length),
      firstTier3Minute: firstTier3,
      finalTechMinute: finalTech,
    },
    economy: (() => {
      const third = Math.floor(cycles / 3);
      const volatility = (from: number) =>
        TRADE_GOOD_IDS.reduce(
          (sum, g) => sum + stdDev(history[g].slice(from).map((r) => Math.log(r))),
          0,
        ) / TRADE_GOOD_IDS.length;
      const perCycle = (xs: readonly number[]) =>
        xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : 0;
      const allTrades = perCycle(tradesPerCycle);
      const inflations = state.players.map(
        (p, i) => (baselineRate[i] ?? CURRENCY.startRate) / p.currency.rate - 1,
      );
      const round = (x: number) => +x.toFixed(4);
      return {
        priceVolatility: round(volatility(0)),
        lateVolatility: round(volatility(cycles - third)),
        lateActivity: round(allTrades > 0 ? perCycle(tradesPerCycle.slice(-third)) / allTrades : 0),
        lateCashShare: round(perCycle(cashShares.slice(-third))),
        inflation: round(inflations.reduce((a, b) => a + b, 0) / inflations.length),
        maxInflation: round(Math.max(...inflations)),
        outputGini: round(gini(playerOutput)),
        ratePinnedShare: round(ratePinned / Math.max(1, cycles * players.length)),
        pricePinnedShare: round(pricePinned / Math.max(1, cycles * TRADE_GOOD_IDS.length)),
      };
    })(),
    players: state.players.map((p, i) => ({
      id: p.id,
      faction: p.faction,
      netWorth: +(
        p.cash * p.currency.rate +
        TRADE_GOOD_IDS.reduce((sum, g) => sum + p.goods[g] * state.market[g].price, 0)
      ).toFixed(2),
      output: +playerOutput[i]!.toFixed(2),
      finalRate: +p.currency.rate.toFixed(4),
      printed: p.currency.printed,
      finalPopulation: +p.planet.population.toFixed(2),
    })),
    currency: (() => {
      const finals = state.players.map((p) => p.currency.rate);
      return {
        minRate: +(Number.isFinite(minRate) ? minRate : CURRENCY.startRate).toFixed(3),
        maxRate: +(maxRate || CURRENCY.startRate).toFixed(3),
        avgFinalRate: +(finals.reduce((a, b) => a + b, 0) / finals.length).toFixed(3),
        finalSpread: +(Math.max(...finals) - Math.min(...finals)).toFixed(3),
        emitted: state.players.reduce((sum, p) => sum + p.currency.printed, 0),
        reserveDeclared,
        reserveLost,
        reserveCycleShare: +(reserveCycles / Math.max(1, cycles)).toFixed(3),
        firstReserveCycle,
        seigniorage: +seigniorage.toFixed(2),
      };
    })(),
    market: {
      trades,
      unitsTraded,
      feesPaid: Math.round(feesPaid * 100) / 100,
      marketWarnings,
      marketShocks,
      priceRatio: Object.fromEntries(
        TRADE_GOOD_IDS.map((g) => [
          g,
          {
            min: +range[g].min.toFixed(4),
            max: +range[g].max.toFixed(4),
            final: +ratio(state, g).toFixed(4),
          },
        ]),
      ) as MarketReport['priceRatio'],
      priceHistory: history,
      violations,
    },
  };
}
